import React, { useState, useEffect, useRef, useCallback } from "react";
import Header from "./Header.js";
import Footer from "./Footer.js";
import Select from "react-select";
import { TailSpin } from "react-loader-spinner";
import "bootstrap-icons/font/bootstrap-icons.css";
import "./Style.css";
import DeleteNotification from "../../Components/Admin_components/delete_notification.js";
import "../../Api/apiUrls";
import { getAdminAppointmentsNotifications } from "../../Api/apiUrls";

function AppointmentNotification() {
  // Modal states
  const [modalDeleteNotificationIsOpen, setDeleteNotificationIsOpen] = useState(false);
  const [selectedNotificationId, setSelectedNotificationId] = useState(null);

  // Filter and search states
  const [searchQuery, setSearchQuery] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  // Data states
  const [allNotifications, setAllNotifications] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [pagination, setPagination] = useState({});

  // Loading states
  const [isLoading, setIsLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);

  const observer = useRef();
  const searchInputRef = useRef();
  const tableContainerRef = useRef();

  const handleSearchInputChange = (event) => {
    setSearchQuery(event.target.value);
    if (event.target.value === "") {
      setSearchTerm("");
      setPage(1);
    }
  };

  const handleSearchSubmit = (e) => {
    if (e) e.preventDefault();
    if (searchQuery !== searchTerm) {
      setSearchTerm(searchQuery);
      setPage(1);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearchSubmit();
    }
  };

  const fetchNotifications = useCallback(async (pageNum = 1, reset = false) => {
    setIsFetching(true);
    try {
      let url = `${getAdminAppointmentsNotifications}?page=${pageNum}&page_size=100`;
      
      if (searchTerm) {
        url += `&search=${encodeURIComponent(searchTerm)}`;
      }

      const response = await fetch(url);
      if (!response.ok) throw new Error("Network response was not ok");
      
      const data = await response.json();
      const newNotifications = data.data.Notification || [];
      
      if (pageNum === 1 || reset) {
        setAllNotifications(newNotifications);
        setPagination(data.data.pagination || {});
        setTotalItems(data.data.pagination?.total_items || 0);
      } else {
        setAllNotifications(prev => [...prev, ...newNotifications]);
      }
      
      setHasMore(newNotifications.length > 0);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsFetching(false);
      setIsLoading(false);
    }
  }, [searchTerm]);

  useEffect(() => {
    fetchNotifications(page);
  }, [page, fetchNotifications]);

  // Handle scroll for infinite loading
  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      if (
        !isFetching && 
        hasMore &&
        container.scrollHeight - container.scrollTop <= container.clientHeight + 100
      ) {
        setPage(prev => prev + 1);
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [isFetching, hasMore]);

  // Reset and fetch when search term changes
  useEffect(() => {
    if (page === 1) {
      fetchNotifications(1, true);
    } else {
      setPage(1);
    }
  }, [searchTerm]);

  const formatCreatedAt = (createdAt) => {
    if (!createdAt) return "--";
    const createdAtDate = new Date(createdAt);
    if (isNaN(createdAtDate)) return "--";
    
    const datePart = createdAtDate.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
    
    const timePart = createdAtDate.toLocaleTimeString("en-GB", {
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    });
    
    return (
      <div>
        <div>{datePart}</div>
        <div>{timePart}</div>
      </div>
    );
  };

  const openDeleteModal = (notificationId) => {
    setSelectedNotificationId(notificationId);
    setDeleteNotificationIsOpen(true);
  };

  const closeDeleteNotification = () => {
    setDeleteNotificationIsOpen(false);
    fetchNotifications(page, true);
  };

  // Calculate showing count
  const showingCount = allNotifications.length;
  const isFiltered = searchTerm;

  return (
    <div>
      <Header />

      <main id="main" className="main">
        <div className="pagetitle">
          <h1>Appointment Notification</h1>
        </div>
        <section className="section">
          <div className="card p-2">
            <div className="parent col-md-12 row mt-2">
              <div className="col-md-8 d-flex flex-wrap">
                <form onSubmit={handleSearchSubmit} className="d-flex">
                  <div className="child mr-2 mb-2">
                    <input
                      ref={searchInputRef}
                      onChange={handleSearchInputChange}
                      onKeyPress={handleKeyPress}
                      value={searchQuery}
                      type="text"
                      className="form-control"
                      placeholder="Search all fields..."
                    />
                  </div>
                  <div className="child mr-2 mb-2">
                    <button 
                      type="submit" 
                      className="btn btn-primary"
                      disabled={isFetching}
                    >
                      {isFetching ? 'Searching...' : 'Search'}
                    </button>
                  </div>
                </form>
              </div>
            </div>

            <div className="table-responsive" ref={tableContainerRef} style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              {isLoading ? (
                <div className="text-center my-5">
                  <TailSpin color="#0d6efd" width={50} />
                </div>
              ) : allNotifications.length > 0 ? (
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th>Sr.no</th>
                      <th>Message</th>
                      <th>From</th>
                      <th>To</th>
                      <th>Created At</th>
                      {/* <th>Action</th> */}
                    </tr>
                  </thead>
                  <tbody>
                    {allNotifications.map((notification, index) => (
                      <tr key={notification.id}>
                        <td>{index + 1}</td>
                        <td>{notification.notification_message || "--"}</td>
                        <td>{notification.sender?.name || "--"}</td>
                        <td>{notification.notification_for?.name || "--"}</td>
                        <td>{formatCreatedAt(notification.created_at)}</td>
                        {/* <td>
                          <button 
                            className="btn btn-danger btn-sm"
                            onClick={() => openDeleteModal(notification.id)}
                          >
                            <i className="bi bi-trash"></i> Delete
                          </button>
                        </td> */}
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="text-center py-4">
                  <h4>No notification found</h4>
                  <p>Try adjusting your search or filters</p>
                </div>
              )}

              {isFetching && (
                <div className="text-center py-3">
                  <TailSpin color="#0d6efd" width={50} />
                </div>
              )}
            </div>

            <div className="p-3 bg-dark text-white rounded-bottom">
              Showing {showingCount} of {totalItems} total notification
              {hasMore && !isFetching && !isFiltered && " - scroll to load more"}
              {hasMore && isFiltered && (
                <button 
                  className="btn btn-sm btn-light ml-2"
                  onClick={() => {
                    const nextPage = page + 1;
                    if (pagination.total_pages && nextPage <= pagination.total_pages) {
                      setPage(nextPage);
                    }
                  }}
                  disabled={isFetching}
                >
                  Load More
                </button>
              )}
            </div>
          </div>
        </section>
      </main>

      {modalDeleteNotificationIsOpen && (
        <DeleteNotification
          notificationId={selectedNotificationId}
          onClose={closeDeleteNotification}
        />
      )}

      <Footer />
    </div>
  );
}

export default AppointmentNotification;